/**
 * Trial countdown utilities for the in-app trial indicator banner.
 * Calculates remaining days and generates display copy.
 */

/**
 * Calculate the number of days remaining in the trial (calendar days).
 * @param trialEnd - ISO 8601 datetime string or null
 * @returns Number of calendar days remaining (≥ 0), or 0 if expired/invalid
 */
export function daysRemaining(trialEnd: string | null | undefined): number {
  if (!trialEnd) return 0;

  try {
    const now = new Date();
    const endDate = new Date(trialEnd);
    
    // Normalize both to midnight UTC for calendar day comparison
    const todayMidnight = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const endMidnight = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate()));
    
    const msPerDay = 86_400_000;
    const daysDiff = Math.ceil((endMidnight.getTime() - todayMidnight.getTime()) / msPerDay);
    
    return Math.max(daysDiff, 0);
  } catch {
    return 0;
  }
}

/**
 * Format a date for display in the trial banner.
 * @param trialEnd - ISO 8601 datetime string
 * @returns Formatted date string (e.g., "Jan 22, 2026")
 */
function formatTrialEndDate(trialEnd: string): string {
  try {
    const date = new Date(trialEnd);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

/**
 * Get the banner copy for the trial indicator.
 *
 * @param daysLeft - Number of days remaining in trial
 * @param trialEnd - ISO 8601 datetime string for the trial end date
 * @returns Object with text for the banner
 */
export interface TrialBannerContent {
  text: string;
}

export function getTrialBannerContent(
  daysLeft: number,
  trialEnd: string
): TrialBannerContent {
  const trialEndDate = formatTrialEndDate(trialEnd);
  const dayLabel = daysLeft === 1 ? 'day' : 'days';

  return {
    text: `Your trial ends on **${trialEndDate}**. **${daysLeft} ${dayLabel} left**.`,
  };
}
